// @flow
import { useQuery } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ResidentService from '../../services/residentService';
import useSearchParams from './hooks.useSearchParams';

/**
 * Returns the query key for a household's draft unit transfer.
 */
export const draftUnitTransferKey = (
  organizationId: string,
  propertyId: string,
  householdId: string,
) => ['draftUnitTransfer', organizationId, propertyId, householdId];

/**
 * ENG-30316: fetches the household's Draft UnitTransfer so the Manage Units screen knows the
 * selected Transfer Date and can compare it against each unit's prior household move-out date.
 * Deliberately not the move-in ready date: that value is clamped to today, which would reject
 * every backdated transfer.
 *
 * The query is the single place the restriction is switched off: with the `unitTransfers` flag
 * off, or outside a transfer, it never runs, `transferDate` stays undefined, and the occupancy
 * predicate short-circuits — so flag-off behavior is byte-identical to today (Sc4).
 *
 * `householdId` is empty on the first render (it resolves via useApplicantOrResidentToAssignUnit),
 * hence the id guards in `enabled`.
 */
export default function useDraftUnitTransfer({
  organizationId,
  propertyId,
  householdId,
}: {
  organizationId: string,
  propertyId: string,
  householdId: string,
}) {
  const { isTransfer } = useSearchParams();
  const { unitTransfers: unitTransfersFlag } = useFlags();

  return useQuery(
    draftUnitTransferKey(organizationId, propertyId, householdId),
    () => {
      const service = new ResidentService();
      return service.getDraftUnitTransfer(
        organizationId,
        propertyId,
        householdId,
      );
    },
    {
      enabled: Boolean(
        unitTransfersFlag &&
          isTransfer &&
          organizationId &&
          propertyId &&
          householdId,
      ),
      // The draft's date can be changed elsewhere (the Resident Transfer card's Edit modal)
      // between visits, so re-read on view rather than trusting a stale cache.
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: false,
    },
  );
}
